import type { UserPermission } from '@repo/permissions'
import {
  ArmchairIcon,
  BellIcon,
  BoxIcon,
  CalculatorIcon,
  ChartNoAxesCombinedIcon,
  CloudDownloadIcon,
  CoinsIcon,
  DollarSignIcon,
  FlaskConicalIcon,
  FolderClockIcon,
  FolderDownIcon,
  FoldersIcon,
  FolderUpIcon,
  GitCompareArrowsIcon,
  HandCoinsIcon,
  HandshakeIcon,
  KeyRoundIcon,
  LightbulbIcon,
  PercentIcon,
  ReceiptTextIcon,
  ScaleIcon,
  SettingsIcon,
  StoreIcon,
  TrendingUpDownIcon,
  UserRoundIcon,
  UserRoundPlusIcon,
  UsersIcon,
  WalletIcon,
  type LucideIcon,
} from 'lucide-react'

export type CrtRouteEntry = {
  title: string
  crumb?: string
  /** False for a section with no page of its own (its index redirects away). */
  isCrumbLinkable?: boolean
  icon?: LucideIcon
  permissions?: UserPermission[]
  keywords?: string[]
  paramName?: string
}

export function toStaticData(entry: CrtRouteEntry) {
  return {
    title: entry.title,
    crumb: entry.crumb ?? entry.title,
    isCrumbLinkable: entry.isCrumbLinkable,
    icon: entry.icon,
    permissions: entry.permissions,
  }
}

export const CRT_ROUTES = {
  // ── Root ──
  '/crt': {
    title: 'route.crt',
  },

  // ── Welcome ──
  '/crt/welcome': {
    title: 'route.welcome',
    crumb: 'breadcrumbs.welcomeCrt',
    icon: ArmchairIcon,
  },

  // ── KPI Reports ──
  // The parent is a title/icon holder for the sidebar section; both children read the same two summary
  // endpoints, so they share its permissions.
  '/crt/kpi-reports': {
    title: 'route.crt.consolidatedRateTable',
    icon: ChartNoAxesCombinedIcon,
    permissions: ['crt_pnl_actuals_view', 'crt_pnl_forecasts_view'],
  },
  '/crt/kpi-reports/profits-and-loss-summary': {
    title: 'route.crt.profitsAndLossSummary',
    icon: ScaleIcon,
    permissions: ['crt_pnl_actuals_view', 'crt_pnl_forecasts_view'],
    keywords: ['kpi', 'summary', 'p&l', 'pl', 'actuals', 'forecasts', 'gross', 'net'],
  },
  '/crt/kpi-reports/cash-dashboard': {
    title: 'route.crt.cashDashboardShort',
    crumb: 'route.crt.cashDashboard',
    icon: CoinsIcon,
    permissions: ['crt_pnl_actuals_view', 'crt_pnl_forecasts_view'],
    keywords: ['cash', 'dashboard', 'ar', 'receivable', 'gto', 'nto', 'ptt', 'vat', 'excise'],
  },

  // ── Variance Analysis ──
  '/crt/variance-analysis': {
    title: 'route.crt.varianceAnalysis',
    icon: GitCompareArrowsIcon,
    permissions: ['crt_variance_analysis_view'],
    keywords: ['variance', 'analysis', 'compare', 'comparison', 'price', 'volume', 'mix'],
  },

  // The drill pages. Each is a step down from the heat map and only means anything against the
  // comparison and cell its search params carry, so none is a destination in its own right: they are
  // listed for their title and icon — the breadcrumbs and the sidebar read those — and deliberately
  // left out of the command menu, which would otherwise offer a way in with no comparison to show.
  '/crt/variance-analysis/by-cost-type': {
    title: 'route.crt.varianceAnalysisByCostType',
    permissions: ['crt_variance_analysis_view'],
    icon: GitCompareArrowsIcon,
  },
  '/crt/variance-analysis/by-budget': {
    title: 'route.crt.varianceAnalysisByBudget',
    permissions: ['crt_variance_analysis_view'],
    icon: GitCompareArrowsIcon,
  },
  '/crt/variance-analysis/by-agreement': {
    title: 'route.crt.varianceAnalysisByAgreement',
    permissions: ['crt_variance_analysis_view'],
    icon: GitCompareArrowsIcon,
  },
  '/crt/variance-analysis/by-banner': {
    title: 'route.crt.varianceAnalysisByBanner',
    permissions: ['crt_variance_analysis_view'],
    icon: GitCompareArrowsIcon,
  },

  // ── Materials ──
  '/crt/materials': {
    title: 'route.materials',
    icon: BoxIcon,
  },
  '/crt/materials/materials-list': {
    title: 'route.materials.materialsList',
    icon: BoxIcon,
    permissions: ['crt_materials_materials_list_view'],
  },
  '/crt/materials/price-list': {
    title: 'route.materials.priceList',
    icon: BoxIcon,
    permissions: ['crt_materials_price_list_view'],
  },
  '/crt/materials/brand-variants': {
    title: 'route.crt.brandVariants',
    icon: BoxIcon,
    permissions: ['crt_brand_variants_view'],
  },
  '/crt/materials/insights': {
    title: 'route.crt.insights',
    icon: LightbulbIcon,
    permissions: [
      'crt_product_type_variant_view',
      'crt_federal_excise_view',
      'crt_unreleased_materials_view',
      'crt_product_types_view',
    ],
  },
  '/crt/materials/insights/product-type-variants': {
    title: 'route.crt.productTypeVariants',
    icon: CloudDownloadIcon,
    permissions: ['crt_product_type_variant_view'],
    keywords: ['product', 'types'],
  },
  '/crt/materials/insights/unreleased-materials': {
    title: 'route.crt.unreleasedMaterials',
    icon: FolderUpIcon,
    permissions: ['crt_unreleased_materials_view'],
    keywords: ['unreleased', 'materials'],
  },
  '/crt/materials/insights/federal-excise': {
    title: 'route.crt.federalExcise',
    icon: FolderUpIcon,
    permissions: ['crt_federal_excise_view'],
    keywords: ['federal', 'excise', 'tax', 'mapping'],
  },
  '/crt/materials/insights/product-types': {
    title: 'route.crt.productTypes',
    icon: FolderUpIcon,
    permissions: ['crt_product_types_view'],
    keywords: ['product', 'types', 'mapping'],
  },
  '/crt/materials/insights/material-groups-overrides': {
    title: 'route.crt.materialGroupOverrides',
    icon: FolderUpIcon,
    permissions: ['crt_material_group_overrides_view'],
    keywords: ['material', 'group', 'override', 'overrides'],
  },

  // ── Pricing & Excise ──
  '/crt/pricing-excise': {
    title: 'route.crt.pricingExcise',
    icon: CalculatorIcon,
    permissions: ['crt_pe_pricing_assumptions_view'],
  },
  '/crt/pricing-excise/pricing-assumptions': {
    title: 'route.crt.pricingExcise.pricingAssumptions',
    icon: CalculatorIcon,
    permissions: ['crt_pe_pricing_assumptions_view'],
  },
  '/crt/pricing-excise/tax-assumptions': {
    title: 'route.crt.pricingExcise.taxAssumptions',
    icon: CalculatorIcon,
    permissions: ['crt_pe_tax_assumptions_view'],
  },
  '/crt/pricing-excise/pricing-rate-table': {
    title: 'route.crt.pricingExcise.pricingRateTableShort',
    crumb: 'route.crt.pricingExcise.pricingRateTable',
    icon: CalculatorIcon,
    permissions: ['crt_pe_pricing_rate_table_view'],
  },
  '/crt/pricing-excise/insights': {
    title: 'route.crt.insights',
    icon: LightbulbIcon,
    permissions: [
      'crt_pe_pricing_assumptions_view',
      'crt_pe_tax_assumptions_view',
      'crt_retailer_rates_view',
      'crt_consumer_rates_view',
      'crt_channel_mix_view',
    ],
  },
  '/crt/pricing-excise/insights/channel-mix': {
    title: 'route.crt.channelMix',
    icon: CloudDownloadIcon,
    permissions: ['crt_channel_mix_view'],
    keywords: ['channel', 'mix'],
  },
  '/crt/pricing-excise/insights/retailer-rates': {
    title: 'route.crt.retailerRates',
    icon: FolderUpIcon,
    permissions: ['crt_retailer_rates_view'],
    keywords: ['retailer', 'rates'],
  },
  '/crt/pricing-excise/insights/consumer-rates': {
    title: 'route.crt.consumerRates',
    icon: FolderUpIcon,
    permissions: ['crt_consumer_rates_view'],
    keywords: ['consumer', 'rates'],
  },

  // ── Volumes ──
  '/crt/volumes': {
    title: 'route.crt.volumes',
    icon: TrendingUpDownIcon,
    permissions: ['crt_volume_forecasts_view', 'crt_volume_scenarios_view'],
  },
  '/crt/volume-scenarios': {
    title: 'route.crt.volumeScenarios',
    icon: FlaskConicalIcon,
    permissions: ['crt_volume_scenarios_view'],
    keywords: ['volume', 'scenarios', 'what-if', 'target', 'forecast'],
    paramName: 'volumeScenarioId',
  },

  // ── Volume Forecasts ──
  '/crt/volume-forecasts': {
    title: 'route.crt.volumeForecasts',
    icon: TrendingUpDownIcon,
    permissions: ['crt_volume_forecasts_view'],
  },
  '/crt/volume-forecasts/nc-provincial-contributions': {
    title: 'route.crt.ncProvincialContributionsShort',
    crumb: 'route.crt.ncProvincialContributions',
    icon: TrendingUpDownIcon,
    permissions: ['crt_volume_forecasts_configure'],
    keywords: ['nc', 'provincial', 'contributions', 'non-combustible', 'configuration'],
  },
  '/crt/volume-forecasts/channel-contributions': {
    title: 'route.crt.channelContributions',
    icon: TrendingUpDownIcon,
    permissions: ['crt_volume_forecasts_configure'],
    keywords: ['channel', 'contributions', 'volume', 'forecast', 'configuration'],
  },
  '/crt/volume-forecasts/sub-channel-allocations': {
    title: 'route.crt.subChannelAllocationsShort',
    crumb: 'route.crt.subChannelAllocations',
    icon: TrendingUpDownIcon,
    permissions: ['crt_volume_forecasts_configure'],
    keywords: ['sub-channel', 'allocations', 'volume', 'forecast', 'configuration'],
  },

  // ── Customer Investments ──
  '/crt/customer-investments': {
    title: 'route.crt.customerInvestments',
    icon: WalletIcon,
    permissions: [
      'crt_agreements_view',
      'crt_rap_forecasts_view',
      'crt_sales_deal_actuals_view',
      'crt_distribution_margin_mix_view',
      'crt_investments_view',
      'crt_budget_tracker_view',
      'crt_key_accounts_view',
    ],
  },
  '/crt/customer-investments/insights': {
    title: 'route.crt.insights',
    icon: LightbulbIcon,
    permissions: [
      'crt_agreements_view',
      'crt_agreement_mappings_view',
      'crt_rap_forecasts_view',
      'crt_sales_deal_actuals_view',
      'crt_distribution_margin_mix_view',
      'crt_budget_allocations_view',
      'crt_ip_accruals_view',
      'crt_calculation_run_accruals_view',
      'crt_retailer_margins_fmc_view',
      'crt_retailer_margins_nc_view',
      'crt_wholesaler_margins_fmc_view',
      'crt_wholesaler_margins_nc_view',
      'crt_investments_view',
      'crt_budget_tracker_view',
      'crt_key_accounts_view',
    ],
  },
  '/crt/customer-investments/key-accounts': {
    title: 'route.crt.keyAccountsTracker',
    icon: StoreIcon,
    permissions: ['crt_key_accounts_view'],
    keywords: ['key', 'accounts', 'hierarchy'],
  },
  '/crt/customer-investments/investments': {
    title: 'route.crt.investments',
    icon: WalletIcon,
    permissions: ['crt_investments_view'],
    keywords: [
      'investments',
      'actuals',
      'forecast',
      'forecasts',
      'rap',
      'consolidated',
      'sales',
      'deals',
      'ip',
      'accruals',
    ],
  },
  '/crt/customer-investments/budget-tracker': {
    title: 'route.crt.budgetTracker',
    icon: WalletIcon,
    permissions: ['crt_budget_tracker_view'],
    keywords: ['budget', 'tracker', 'left to spend', 'actuals', 'forecast', 'bucket'],
  },
  '/crt/customer-investments/insights/manual-investments': {
    title: 'route.crt.manualInvestments',
    icon: HandCoinsIcon,
    permissions: ['crt_manual_investments_view', 'crt_investments_view'],
    keywords: ['manual', 'investments', 'program', 'vistex'],
    paramName: 'manualInvestmentId',
  },
  '/crt/customer-investments/key-accounts/estimates': {
    title: 'route.crt.keyAccountContractEstimates',
    icon: DollarSignIcon,
    permissions: ['crt_key_account_contract_estimates_view'],
    keywords: ['key', 'accounts', 'estimates', 'contract'],
  },
  '/crt/customer-investments/insights/agreements': {
    title: 'route.crt.agreements',
    icon: CloudDownloadIcon,
    permissions: ['crt_agreements_view'],
    keywords: ['agreements'],
  },
  '/crt/customer-investments/insights/rap-forecasts': {
    title: 'route.crt.rapForecasts',
    icon: CloudDownloadIcon,
    permissions: ['crt_rap_forecasts_view', 'crt_budget_tracker_view'],
    keywords: ['rap', 'forecasts'],
    paramName: 'rapForecastId',
  },
  '/crt/customer-investments/insights/sales-deal-actuals': {
    title: 'route.crt.salesDealActuals',
    icon: CloudDownloadIcon,
    permissions: [
      'crt_sales_deal_actuals_view',
      'crt_investments_view',
      'crt_budget_tracker_view',
      'crt_key_accounts_view',
    ],
    keywords: ['sales', 'deal', 'actuals'],
    paramName: 'salesDealActualId',
  },
  '/crt/customer-investments/insights/distribution-margin-mix': {
    title: 'route.crt.distributionMarginMixShort',
    crumb: 'route.crt.distributionMarginMix',
    icon: CloudDownloadIcon,
    permissions: ['crt_distribution_margin_mix_view'],
    keywords: ['distribution', 'margin', 'mix'],
    paramName: 'distributionMarginMixId',
  },
  '/crt/customer-investments/insights/distribution-margin-mix/banner-mix': {
    title: 'route.crt.distributionBannerMix',
    icon: PercentIcon,
    permissions: ['crt_distribution_banner_mix_view'],
    keywords: ['distribution', 'banner', 'mix', 'assumption'],
  },
  '/crt/customer-investments/insights/ip-accruals': {
    title: 'route.crt.ipAccruals',
    icon: CloudDownloadIcon,
    permissions: [
      'crt_ip_accruals_view',
      'crt_investments_view',
      'crt_budget_tracker_view',
      'crt_key_accounts_view',
    ],
    keywords: ['ip', 'accruals'],
    paramName: 'ipAccrualId',
  },
  '/crt/customer-investments/insights/calculation-run-accruals': {
    title: 'route.crt.calculationRunAccruals',
    icon: CloudDownloadIcon,
    permissions: [
      'crt_calculation_run_accruals_view',
      'crt_investments_view',
      'crt_budget_tracker_view',
      'crt_key_accounts_view',
    ],
    keywords: ['calculation', 'run', 'accruals'],
    paramName: 'calculationRunAccrualId',
  },
  '/crt/customer-investments/insights/budget-allocations': {
    title: 'route.crt.budgetAllocations',
    icon: CloudDownloadIcon,
    permissions: ['crt_budget_allocations_view', 'crt_budget_tracker_view'],
    keywords: ['budget', 'allocations'],
  },
  '/crt/customer-investments/insights/retailer-margins/fmc': {
    title: 'route.crt.retailerMarginsFmc',
    icon: FolderUpIcon,
    permissions: ['crt_retailer_margins_fmc_view'],
    keywords: ['retailer', 'margins', 'fmc'],
  },
  '/crt/customer-investments/insights/retailer-margins/nc': {
    title: 'route.crt.retailerMarginsNc',
    icon: FolderUpIcon,
    permissions: ['crt_retailer_margins_nc_view'],
    keywords: ['retailer', 'margins', 'nc'],
  },
  '/crt/customer-investments/insights/wholesaler-margins/fmc': {
    title: 'route.crt.wholesalerMarginsFmc',
    icon: FolderUpIcon,
    permissions: ['crt_wholesaler_margins_fmc_view'],
    keywords: ['wholesaler', 'margins', 'fmc'],
  },
  '/crt/customer-investments/insights/wholesaler-margins/nc': {
    title: 'route.crt.wholesalerMarginsNc',
    icon: FolderUpIcon,
    permissions: ['crt_wholesaler_margins_nc_view'],
    keywords: ['wholesaler', 'margins', 'nc'],
  },
  '/crt/customer-investments/insights/master-agreements': {
    title: 'route.crt.masterAgreements',
    icon: HandshakeIcon,
    permissions: ['crt_agreement_mappings_view'],
    keywords: ['master', 'agreements', 'investment', 'types', 'mapping'],
  },

  // ── Customers ──
  '/crt/customers': {
    title: 'route.crt.customers',
    icon: StoreIcon,
    permissions: ['crt_commercial_partners_view'],
  },
  '/crt/customers/commercial-partners': {
    title: 'route.crt.commercialPartners',
    icon: StoreIcon,
    permissions: ['crt_commercial_partners_view'],
    keywords: ['commercial', 'partners', 'banners', 'accounts'],
  },

  // ── Cost of Goods ──
  '/crt/cost-of-goods': {
    title: 'route.crt.costOfGoods',
    icon: DollarSignIcon,
    permissions: ['crt_cost_of_goods_view'],
  },

  // ── BPC Actuals ──
  '/crt/bpc-actuals': {
    title: 'route.crt.bpcActuals',
    icon: ReceiptTextIcon,
    permissions: ['crt_bpc_actuals_view'],
  },

  // ── Admin ──
  '/crt/admin': {
    title: 'Admin',
    icon: UserRoundIcon,
    isCrumbLinkable: false,
  },
  '/crt/admin/users': {
    title: 'route.admin.users',
    icon: UsersIcon,
    permissions: ['crt_users_view'],
  },
  '/crt/admin/invitations': {
    title: 'Invitations',
    icon: UserRoundPlusIcon,
    permissions: ['crt_invites_view'],
  },
  '/crt/admin/roles': {
    title: 'route.admin.roles',
    icon: KeyRoundIcon,
    permissions: ['crt_roles_view'],
  },
  '/crt/admin/audit-trails': {
    title: 'route.admin.auditTrails',
    icon: FolderClockIcon,
    permissions: ['crt_audit_trails_view'],
  },
  '/crt/admin/notifications': {
    title: 'route.admin.notifications',
    icon: BellIcon,
    permissions: ['crt_notifications_view'],
    keywords: ['notifications', 'email', 'subscriptions', 'alerts'],
  },

  // ── Uploads ──
  '/crt/my-uploads': {
    title: 'route.uploads',
    icon: FoldersIcon,
    permissions: ['crt_upload_myUpload_view'],
  },

  // ── My Exports ──
  '/crt/my-exports': {
    title: 'route.myExports',
    icon: FolderDownIcon,
    keywords: ['my', 'exports', 'downloads', 'csv'],
    permissions: ['crt_async_exports_view'],
  },

  // ── Azure Imports ──
  '/crt/azure-imports': {
    title: 'route.azureImports',
    icon: FoldersIcon,
    permissions: ['crt_azure_imports_view'],
  },

  // ── Settings ──
  // App-wide settings (currency rates, full-year targets); the page shows the sections a user may use.
  '/crt/settings': {
    title: 'route.crt.settings',
    icon: SettingsIcon,
    permissions: ['crt_currency_rates_manage', 'crt_lmc_targets_view'],
    keywords: ['currency', 'currencies', 'rates', 'exchange', 'targets', 'lmc', 'full year'],
  },
} as const satisfies Record<string, CrtRouteEntry>

export type CrtRoutePath = keyof typeof CRT_ROUTES
